import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import useApiClient from '../services/api';

const SubHeader = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const location = window.location;
  const pathname = location.pathname;
  const searchParams = new URLSearchParams(location.search);
  const [projectIdFromTranslation, setProjectIdFromTranslation] = useState(null);
  const [projectName, setProjectName] = useState(null);
  const [documentTitle, setDocumentTitle] = useState(null);
  const [translationStatus, setTranslationStatus] = useState(null);
  const [translationProgress, setTranslationProgress] = useState(0);
  const apiClient = useApiClient();

  // Fetch project details function
  const fetchProjectDetails = async (projectId) => {
    if (!projectId || projectId === 'unknown') return;

    try {
      const response = await apiClient.get(`/projects/${projectId}`);
      const projectData = response.data.project || response.data;

      if (projectData.name) {
        console.log('[SubHeader] Found project name:', projectData.name);
        setProjectName(projectData.name);
      }
    } catch (error) {
      console.error('[SubHeader] Error fetching project details:', error);
    }
  };

  // Fetch translation details function
  const fetchTranslationDetails = async () => {
    const translationMatch = pathname.match(/\/translator\/(setup|edit)\/([^/]+)/);
    if (!translationMatch) {
      setProjectIdFromTranslation(null);
      setDocumentTitle(null);
      setTranslationStatus(null);
      setTranslationProgress(0);
      return;
    }

    const documentId = translationMatch[2];
    console.log('[SubHeader] Fetching translation status for documentId:', documentId);

    try {
      // Check if projectId is in query params first
      const queryProjectId = searchParams.get('projectId');
      if (queryProjectId && !projectIdFromTranslation) {
        console.log('[SubHeader] Found projectId in query params:', queryProjectId);
        setProjectIdFromTranslation(queryProjectId);
      }

      // Fetch translation status to get project ID and document title
      const response = await apiClient.get(`/translations/${documentId}/status`);
      console.log('[SubHeader] Translation status response:', response.data);

      if (response.data.success && response.data.translation) {
        const translation = response.data.translation;

        // Extract project ID and name
        if (translation.project?.id) {
          const extractedProjectId = translation.project.id;
          console.log('[SubHeader] Found projectId:', extractedProjectId);
          setProjectIdFromTranslation(extractedProjectId);

          // Also extract project name if available
          if (translation.project.name) {
            console.log('[SubHeader] Found project name:', translation.project.name);
            setProjectName(translation.project.name);
          }
        } else {
          console.log('[SubHeader] No projectId found in translation status');
        }

        // Extract document title
        if (translation.originalFileName) {
          setDocumentTitle(translation.originalFileName);
        }

        // Extract status and progress
        if (translation.status) {
          console.log('[SubHeader] Translation status:', translation.status, 'Progress:', translation.progress);
          setTranslationStatus(translation.status);
        }
        if (translation.progress !== undefined) {
          setTranslationProgress(translation.progress);
        }
      }
    } catch (error) {
      console.error('[SubHeader] Error fetching translation details:', error);
    }
  };

  // Initial fetch when page loads or changes
  useEffect(() => {
    if (apiClient) {
      fetchTranslationDetails();

      // Also fetch project details if we're on a project page
      const projectMatch = pathname.match(/\/project\/([^/]+)/);
      const projectId = projectMatch ? projectMatch[1] : searchParams.get('projectId');

      if (projectId) {
        fetchProjectDetails(projectId);
      }
    }
  }, [pathname, searchParams, apiClient]);

  // Fetch project details when projectIdFromTranslation changes
  useEffect(() => {
    if (apiClient && projectIdFromTranslation && !projectName) {
      fetchProjectDetails(projectIdFromTranslation);
    }
  }, [projectIdFromTranslation, apiClient, projectName]);

  // Polling effect - polls every 6 seconds when translation is processing
  useEffect(() => {
    let pollInterval = null;

    // Check if we're on translation pages
    const isOnTranslationPage = pathname.includes('/translator/setup') || pathname.includes('/translator/edit');

    // Only start polling if we're on translation page AND status is processing
    if (isOnTranslationPage && translationStatus?.toLowerCase() === 'processing' && apiClient) {
      console.log('[SubHeader] Starting polling for translation progress...');

      // Poll every 6 seconds
      pollInterval = setInterval(() => {
        fetchTranslationDetails();
      }, 6000);
    }

    // Cleanup: Stop polling when conditions change or component unmounts
    return () => {
      if (pollInterval) {
        console.log('[SubHeader] Stopping polling');
        clearInterval(pollInterval);
      }
    };
  }, [translationStatus, pathname, apiClient]);

  // Check if we're on translation pages
  const isOnTranslationPage = pathname.includes('/translator/setup') || pathname.includes('/translator/edit');

  // Build breadcrumb based on current path
  const getBreadcrumbs = () => {
    const crumbs = [];

    // Always start with Dashboard
    crumbs.push({ label: t('subHeader.dashboard'), path: '/translator/dashboard' });

    // Check if we're on a project page
    const projectMatch = pathname.match(/\/project\/([^/]+)/);
    const projectId = projectMatch ? projectMatch[1] : searchParams.get('projectId') || projectIdFromTranslation;

    // If we have a projectId OR we're on a translation page, add Project breadcrumb
    if (projectId || isOnTranslationPage) {
      const finalProjectId = projectId || 'unknown'; // Use 'unknown' as fallback if still loading
      crumbs.push({
        label: projectName || t('subHeader.project'), // Use project name if available, otherwise fallback to 'Project'
        path: projectId ? `/project/${finalProjectId}` : '#', // Use '#' if no projectId yet
        isActive: !!projectMatch // Active only if we're directly on the project page
      });
    }

    // Add Translation breadcrumb if on translation page
    if (isOnTranslationPage) {
      const translationMatch = pathname.match(/\/(setup|edit)\/([^/]+)/);
      if (translationMatch) {
        crumbs.push({
          label: documentTitle || t('subHeader.translation'),
          path: pathname,
          isActive: true
        });
      }
    }

    // Check if we're on project create page
    if (pathname === '/translator/project/create') {
      crumbs.push({ label: t('subHeader.newProject'), path: pathname, isActive: true });
    }

    // Check if we're on translation create page
    if (pathname === '/translator/create') {
      crumbs.push({ label: t('subHeader.newTranslation'), path: pathname, isActive: true });
    }

    return crumbs;
  };

  const breadcrumbs = getBreadcrumbs();

  return (
    <div className="bg-gradient-to-r from-gray-50 to-gray-100 border-b border-gray-200 sticky top-12 z-30 shadow-sm">
      <div className=" mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-10">
          {/* Left Breadcrumb Navigation */}
          <nav className="flex items-center space-x-1">
            {breadcrumbs.map((crumb, index) => (
              <React.Fragment key={crumb.path}>
                {index > 0 && (
                  <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                )}
                <button
                  onClick={() => !crumb.isActive && navigate(crumb.path)}
                  disabled={crumb.isActive}
                  className={`px-3 py-1.5 text-xs font-medium rounded-md transition-all ${
                    crumb.isActive
                      ? 'text-blue-600 bg-white cursor-default'
                      : 'text-gray-700 hover:text-blue-600 hover:bg-white'
                  }`}
                >
                  {crumb.label}
                </button>
              </React.Fragment>
            ))}
          </nav>

          {/* Center Progress Bar - Only show on translation pages when processing */}
          {isOnTranslationPage && translationStatus?.toLowerCase() === 'processing' && (
            <div className="flex-1 max-w-md mx-6">
              <div className="flex items-center space-x-2">
                <span className="text-xs font-medium text-gray-600 whitespace-nowrap">{t('subHeader.translating')}</span>
                <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-blue-500 via-blue-600 to-blue-700 rounded-full transition-all duration-500 ease-out shadow-sm"
                    style={{ width: `${translationProgress}%` }}
                  >
                    <div className="h-full w-full bg-gradient-to-r from-transparent via-white to-transparent opacity-20 animate-pulse"></div>
                  </div>
                </div>
                <span className="text-xs font-semibold text-blue-600 whitespace-nowrap">{translationProgress}%</span>
              </div>
            </div>
          )}
          {(!isOnTranslationPage || translationStatus?.toLowerCase() !== 'processing') && <div className="flex-1 max-w-md mx-6"></div>}

          {/* Right Action Buttons */}
          <div className="flex items-center space-x-2">
            {/* New Translation Button - Only visible on translation pages */}
            {isOnTranslationPage && (
              <button
                onClick={() => {
                  const projectMatch = pathname.match(/\/project\/([^/]+)/);
                  const projectId = projectMatch ? projectMatch[1] : searchParams.get('projectId') || projectIdFromTranslation;

                  if (projectId) {
                    navigate(`/translator/setup?projectId=${projectId}`);
                  } else {
                    navigate('/translator/setup');
                  }
                }}
                className="px-3 py-1.5 text-xs font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md transition-all shadow-sm hover:shadow"
              >
                {t('subHeader.newTranslation')}
              </button>
            )}

            {/* New Project Button - Always visible */}
            <button
              onClick={() => navigate('/translator/project/create')}
              className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white hover:bg-gray-50 border border-gray-300 rounded-md transition-all shadow-sm hover:shadow"
            >
              {t('subHeader.newProject')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SubHeader;
